import { z } from 'zod'
import './registry'
import { MoneySchema, CurrencyCodeSchema } from './common'

export const DiscogsSearchQuerySchema = z
  .object({
    artist: z.string().optional(),
    title: z.string().optional(),
    catno: z.string().optional(),
    /**
     * Alternative catalogue-number readings from Claude Vision, newline-joined
     * (`\n`). HTTP API v2 has no multi-value query support, so a delimited
     * string is the simplest robust transport. The handler splits, trims, and
     * unions these with `catno` before handing them to the matcher.
     */
    catnoCandidates: z.string().optional(),
    year: z.string().optional(),
  })
  .openapi('DiscogsSearchQuery')

/**
 * How strongly a hit's catalogue number matched the scanned reading(s). Mirrors
 * the `MatchTier` union in lib/catno-match.ts (parity pinned by a unit test).
 * EXACT/CANON are high-confidence (the frontend may auto-load); FUZZY/WEAK
 * always defer to the picker.
 *
 * Deliberately NOT registered as a named OpenAPI component: it's used nullable
 * on a hit (null = no match) but non-nullable inside DiscogsMatch (a match
 * always has a tier). A shared registered component would force one nullability
 * on both; inlining keeps each site's spec accurate.
 */
export const MatchTierSchema = z.enum(['EXACT', 'CANON', 'FUZZY', 'WEAK'])

export const DiscogsHitSchema = z
  .object({
    id: z.number(),
    title: z.string(),
    year: z.union([z.string(), z.number()]).nullable(),
    country: z.string().nullable(),
    catno: z.string().nullable(),
    label: z.array(z.string()).nullable(),
    format: z.array(z.string()).nullable(),
    // External image URLs from Discogs (untrusted), rendered in <img src>. Kept
    // as plain strings — NOT .url() — for two reasons: (1) a malformed Discogs
    // value would otherwise fail response validation and 500 the whole search,
    // and (2) tightening a response property's format is flagged as a breaking
    // change by the oasdiff CI gate. The client already maps "" → null.
    thumb: z.string().nullable(),
    coverImage: z.string().nullable(),
    /**
     * Discogs master-release id this pressing belongs to (the "all versions of
     * this album" grouping), or null when the release has no master. Surfaced so
     * the cascade can expand the pool to every pressing of the album.
     */
    masterId: z.number().nullable(),
    /** Catno-match tier vs. the scanned reading(s); null when nothing matched. */
    matchTier: MatchTierSchema.nullable(),
  })
  .openapi('DiscogsHit')

/**
 * The single best catno match across the whole ranked pool — the frontend uses
 * this to decide whether to auto-load (high-confidence tiers) or open the picker
 * with this pressing pre-highlighted. Null when no candidate matched any hit.
 */
export const DiscogsMatchSchema = z
  .object({
    releaseId: z.number(),
    tier: MatchTierSchema,
    /** The exact reading (most-likely catno or a Vision alternative) that hit. */
    matchedCandidate: z.string(),
  })
  .openapi('DiscogsMatch')

export const DiscogsSearchResponseSchema = z
  .object({
    hits: z.array(DiscogsHitSchema),
    match: DiscogsMatchSchema.nullable(),
  })
  .openapi('DiscogsSearchResponse')

export const GradePriceSchema = z
  .object({
    grade: z.enum(['M', 'NM', 'VG+', 'VG', 'G+', 'G', 'F', 'P']),
    amountEur: MoneySchema,
    originalCurrency: CurrencyCodeSchema,
  })
  .openapi('GradePrice')

export const ReleaseMarketSchema = z
  .object({
    lowestEur: MoneySchema.nullable(),
    medianEur: MoneySchema.nullable(),
    highestEur: MoneySchema.nullable(),
    forSaleCount: z.number().int().nonnegative(),
    /**
     * Best-effort signal of the source currency for the lowest_price field.
     * Discogs marketplace stats aggregates listings worldwide so the three
     * stat fields (low/median/high) can independently use different
     * currencies; the EUR values above have already been converted per-field.
     * UI labels like "live Discogs (USD)" should treat this as the lowest's
     * currency, not a guarantee that median or high used the same one.
     */
    originalCurrency: CurrencyCodeSchema,
  })
  .openapi('ReleaseMarket')

/**
 * Discogs community demand signals: how many users WANT this release (wantlist)
 * vs how many HAVE it (collection). Many wanters over few havers/for-sale
 * copies = scarcity — a cue to price above the suggestion. `have` counts
 * owners, NOT sellers; the for-sale count lives in `market`. Namespaced like
 * `market` so future community fields (e.g. rating) join without churn.
 */
export const ReleaseCommunitySchema = z
  .object({
    want: z.number().int().nonnegative().nullable(),
    have: z.number().int().nonnegative().nullable(),
  })
  .openapi('ReleaseCommunity')

/**
 * One embeddable video from a Discogs release (in practice always YouTube).
 * Snapshotted onto Record at intake, so the caps here bound what a client may
 * persist to DDB — worst case ≈80 KB per record (≈25 KB videos + ≈56 KB
 * tracklist — see ReleaseTrackSchema below; the tracklist dominates, not the
 * videos), realistic releases 1-3 KB.
 */
export const DiscogsVideoSchema = z
  .object({
    // External video URL from Discogs (untrusted). Plain string, NOT .url() —
    // same rationale as DiscogsHitSchema.thumb above: a malformed Discogs value
    // must not 500 the release lookup, and tightening a response property's
    // format trips the oasdiff CI gate. The frontend validates the host and
    // video id before ever using it in an embed src.
    uri: z.string().max(500),
    title: z.string().max(300),
    /** Video length in seconds, or null when Discogs omits it. */
    duration: z.number().int().min(0).max(86400).nullable(),
  })
  .openapi('DiscogsVideo')

/** One tracklist row ("A1" / "Autobahn" / "22:43"). Display-only text. */
export const ReleaseTrackSchema = z
  .object({
    position: z.string().max(20),
    title: z.string().max(300),
    /** Discogs free-text duration, typically "mm:ss"; "" when unknown. */
    duration: z.string().max(20),
  })
  .openapi('ReleaseTrack')

/**
 * The shape of one Discogs release `formats` entry as the deriver needs it —
 * structural, so both the backend client's parsed value and a script's raw
 * JSON fit. Everything optional: sparsely-edited releases miss any field.
 */
export interface DiscogsRawFormat {
  name?: string | null | undefined
  /** Discogs sends the quantity as a STRING ("2"). */
  qty?: string | null | undefined
  descriptions?: string[] | null | undefined
}

/**
 * Discogs `formats` → the catalogue's display format string ('LP', '2×LP',
 * '12"', '7"', '10"'; falls back to the format name for non-vinyl), or null
 * when Discogs sent nothing usable. 'LP' wins over a size description (a
 * 12" LP is filed as an LP in the shop's vocabulary), and the qty prefixes
 * multi-disc sets the way the crates label them ('2×LP').
 *
 * Lives in the schemas barrel — not the API client — because the format
 * backfill script (scripts/backfill-format.ts) must share this exact rule and
 * scripts can only import `@random-vinyl/backend/schemas` (lib/ modules throw
 * at load outside Lambda). Same channel as audit-fanout.
 */
export function deriveVinylFormat(
  formats: readonly DiscogsRawFormat[] | null | undefined
): string | null {
  const f = formats?.[0]
  if (!f) return null
  const desc = (f.descriptions ?? []).map(d => d.toLowerCase())
  const qtyNum = Number(f.qty ?? '1')
  const qty = Number.isFinite(qtyNum) && qtyNum > 1 ? qtyNum : null
  const size = desc.find(d => d === '7"' || d === '10"' || d === '12"') ?? null
  if (desc.includes('lp')) return qty ? `${qty}×LP` : 'LP'
  if (size) return qty ? `${qty}×${size}` : size
  const name = f.name?.trim()
  return name ? (qty ? `${qty}×${name}` : name) : null
}

// A space-led "(n)" that ends the name or is followed by the next artist's
// join (" & ", ", ", " / ", " Feat. "), so every artist in a multi-artist
// `artists_sort` loses its suffix, not only the last one.
const DISCOGS_DISAMBIGUATOR = /\s+\(\d+\)(?=$|[\s,&/])/g

/**
 * Drop Discogs' numeric disambiguator from an artist or label name:
 * "Wings (2)" → "Wings", "MPL (2)" → "MPL".
 *
 * Discogs appends "(2)", "(3)", … to every artist or label whose name an
 * earlier entity already took. It's part of Discogs' own identifier, not of
 * the name printed on the sleeve, so it has no place on our label, in the
 * catalogue or in the blurp prompt (it also kept the artist name from
 * shielding itself in the blurp's discouraged-term check).
 *
 * Artist and label names ONLY, never a title: an album can really be called
 * "Greatest Hits (2)". The picker's search hits keep the raw Discogs text on
 * purpose — there the suffix is what tells two same-named artists apart.
 */
export function stripDiscogsDisambiguator(name: string): string {
  return name.replace(DISCOGS_DISAMBIGUATOR, '').trim()
}

/**
 * A Discogs release image URL, and ONLY that: `https://i.discogs.com/…`.
 *
 * The value is stored on a record and rendered as `<img src>` for every staff
 * member, and at intake it arrives from the client. Pinning the host means a
 * crafted request can't plant an arbitrary URL (a tracking pixel, an internal
 * address) in everyone's catalogue. The URLs are signed by Discogs, which is
 * why they are stored whole rather than rebuilt from the release id.
 */
export const DiscogsCoverUrlSchema = z
  .string()
  .max(1000)
  .regex(/^https:\/\/i\.discogs\.com\//, 'not a Discogs image URL')

/** One Discogs release `images` entry as the picker needs it (structural). */
export interface DiscogsRawImage {
  type?: string | null | undefined
  uri?: string | null | undefined
  uri150?: string | null | undefined
}

/**
 * Discogs release `images` → the sleeve URL the catalogue falls back to when
 * a record has no photo of its own, or null.
 *
 * `type: 'primary'` first, NOT `images[0]`: Discogs orders images by upload,
 * and on plenty of releases the first one is a label or a runout groove.
 * The 600px `uri` rather than the 150px `uri150`: the mobile card shows the
 * sleeve full-width, where 150px is visibly blurry, and each image is fetched
 * once and then browser-cached for a year.
 *
 * Shared by the release endpoint (intake) and scripts/backfill-discogs-cover.ts
 * through the schemas barrel, the same channel as `deriveVinylFormat`.
 */
export function pickDiscogsCoverUrl(
  images: readonly DiscogsRawImage[] | null | undefined
): string | null {
  // Primary first, then Discogs' order; the first usable URL wins, so a
  // primary with a blank or off-host URL can't hide a good secondary image.
  const primaryFirst = [...(images ?? [])].sort(
    (a, b) => Number(b.type === 'primary') - Number(a.type === 'primary')
  )
  return (
    primaryFirst
      .flatMap(i => [i.uri, i.uri150])
      .find(url => !!url && DiscogsCoverUrlSchema.safeParse(url).success) ?? null
  )
}

export const ReleaseDetailSchema = z
  .object({
    id: z.number(),
    artist: z.string(),
    title: z.string(),
    year: z.number().nullable(),
    country: z.string().nullable(),
    catno: z.string().nullable(),
    labelName: z.string().nullable(),
    genres: z.array(z.string()),
    styles: z.array(z.string()),
    notes: z.string().nullable(),
    /**
     * Display format derived from Discogs `formats` ('LP', '2×LP', '12"') —
     * what intake persists onto `Record.format` so the catalogue's Formaat
     * column and the storefront's format facet actually fill. `.default(null)`
     * for the same manual-independent-deploy reason as `community` below.
     */
    format: z.union([z.string(), z.null()]).default(null),
    /**
     * The release's sleeve image (`pickDiscogsCoverUrl`), which intake persists
     * onto `Record.discogsCoverUrl`. `.default(null)` so an older API and a
     * newer frontend (or the reverse) can deploy independently.
     */
    coverUrl: z.union([DiscogsCoverUrlSchema, z.null()]).default(null),
    market: ReleaseMarketSchema,
    gradePrices: z.array(GradePriceSchema),
    /** Server-side suggested price in EUR: VG+ → market median → midpoint → lowest. */
    suggestedPriceEur: MoneySchema.nullable(),
    // `.default(null)` keeps the frontend (which parses responses with this
    // schema) compatible with a backend that predates the field — deploys are
    // manual and independent, so the frontend can go out first or the backend
    // can be rolled back without breaking every release lookup.
    community: ReleaseCommunitySchema.nullable().default(null),
    // `.default([])` for the same manual-independent-deploy reason as
    // `community` above. The `.nullable()`/array wrappers here and on Record
    // wrap the anonymous arrays, never the registered DiscogsVideo/ReleaseTrack
    // components themselves (see the IsoDate lesson in schemas/records.ts).
    videos: z.array(DiscogsVideoSchema).max(30).default([]),
    tracklist: z.array(ReleaseTrackSchema).max(150).default([]),
    discogsUrl: z.string().url(),
  })
  .openapi('ReleaseDetail')

export type DiscogsSearchQuery = z.infer<typeof DiscogsSearchQuerySchema>
export type DiscogsHit = z.infer<typeof DiscogsHitSchema>
export type DiscogsMatch = z.infer<typeof DiscogsMatchSchema>
export type DiscogsSearchResponse = z.infer<typeof DiscogsSearchResponseSchema>
export type GradePrice = z.infer<typeof GradePriceSchema>
export type ReleaseMarket = z.infer<typeof ReleaseMarketSchema>
export type ReleaseCommunity = z.infer<typeof ReleaseCommunitySchema>
export type DiscogsVideo = z.infer<typeof DiscogsVideoSchema>
export type ReleaseTrack = z.infer<typeof ReleaseTrackSchema>
export type ReleaseDetail = z.infer<typeof ReleaseDetailSchema>
